import type { AxiosRequestConfig } from "axios";
import type { Emitter } from "mitt";
import mitt from "mitt";
import { defineStore } from "pinia";
import type { Ref, UnwrapNestedRefs } from "vue";
import { reactive, ref } from "vue";
import { useLanguageStore } from "../../backend/stores/languageStore.ts";
import type { FilterResponse } from "../../responses/FilterResponse";
import type { ProductResponse } from "../../responses/ProductResponse";
import type { ProductVariantPropertyResponse } from "../../responses/ProductVariantPropertyResponse.ts";
import type { ProductVariantResponse } from "../../responses/ProductVariantResponse";
import type { Pagination } from "../../types/Pagination.ts";
import { api } from "../scripts/api";
import { useFilterStore } from "./filter.ts";

type Events = {
    updatedFilter: undefined;
    initPriceFilter: undefined;
    selectedVariantChanged: ProductVariantResponse;
};

export type ProductLayoutType = "grid" | "list";

interface CustomProductResponseWithFilters extends Pagination<ProductResponse> {
    maxPrice: number;
    currentPrice: string;
    filters: FilterResponse[];
}

export type ProductVariantProperties = {
    [key: string]: Array<ProductVariantPropertyResponse>;
};

export const useProductStore = defineStore("product", () => {
    const filterStore = useFilterStore();
    const languageStore = useLanguageStore();

    const events: Emitter<Events> = mitt<Events>();
    const products: Ref<ProductResponse[]> = ref([]);
    const defaultValues = {
        page: 1,
        sort: "order|asc",
    };
    const sort: Ref<string> = ref(defaultValues.sort);
    const page: Ref<number> = ref(defaultValues.page);
    const perPage: Ref<number> = ref(12);
    const perPageOptions: Ref<Array<number>> = ref([12, 24, 36]);
    const productCount: Ref<number> = ref(0);
    const categorySlug: Ref<string> = ref("");
    const priceMax: Ref<number> = ref(100);
    const priceMin: Ref<number> = ref(0);
    const layout: Ref<ProductLayoutType> = ref("grid");
    const currentPrice: Ref<string> = ref("");
    const selectedVariant: Ref<ProductVariantResponse> = ref(null);

    const getProductRequest: UnwrapNestedRefs<{
        timeout: number | null;
        abort: AbortController | null;
    }> = reactive({
        timeout: null,
        abort: null,
    });
    const getPropertiesRequest: UnwrapNestedRefs<{
        timeout: number | null;
        abort: AbortController | null;
    }> = reactive({
        timeout: null,
        abort: null,
    });
    const hasMoreResults = () => {
        return productCount.value >= page.value * perPage.value;
    };

    const getProducts = async () => {
        return new Promise(async (resolve, reject) => {
            if (getProductRequest.abort !== null) {
                getProductRequest.abort.abort();
            }

            getProductRequest.abort = new AbortController();

            try {
                const sortParams: { [key: string]: string } = {};
                // if(defaultValues.page !== page.value) sortParams['page'] = page.value;
                if (defaultValues.sort !== sort.value) {
                    sortParams.sort = sort.value;
                }

                filterStore.toUrlString(page.value, sortParams, false, true);
                const filterString = filterStore.toRawUrlString(
                    page.value,
                    sortParams,
                );
                const category = categorySlug.value
                    ? `/${categorySlug.value}`
                    : "";

                const response =
                    await api().post<CustomProductResponseWithFilters>(
                        `/site/products${category}/${filterString}`,
                        {
                            lang: languageStore.currentIso(),
                            perPage: perPage.value,
                        },
                        {
                            signal: getProductRequest.abort.signal,
                        } as AxiosRequestConfig,
                    );

                priceMax.value = response.data.maxPrice;
                if (response.data.currentPrice) {
                    currentPrice.value = response.data.currentPrice.value;
                }
                events.emit("initPriceFilter");
                setProducts(response.data);
                setCount(response.data.meta.total);
                filterStore.setFilterOptions(response.data.filters);
                resolve(response.data.data);
            } catch (e) {
                reject(e);
            }
        });
    };

    const getVariantProperties = (
        variant: ProductVariantResponse,
    ): Promise<ProductVariantProperties> => {
        return new Promise(async (resolve, reject) => {
            if (!variant) {
                reject(new Error("Variant is not defined."));
                return;
            }

            if (getPropertiesRequest.abort !== null) {
                getPropertiesRequest.abort.abort();
            }

            getPropertiesRequest.abort = new AbortController();

            try {
                const response = await api().post<ProductVariantProperties>(
                    `/site/product-variant/properties/`,
                    {
                        lang: languageStore.currentIso(),
                        product_variant_id: variant.id,
                    },
                    {
                        signal: getPropertiesRequest.abort.signal,
                    } as AxiosRequestConfig,
                );

                if (!Object.hasOwn(response, "data")) {
                    reject(response);
                    return;
                }

                resolve(response.data);
                return;
            } catch (e) {
                reject(e);
                return;
            }
        });
    };

    const setProducts = (newProducts: ProductResponse[]) => {
        products.value = newProducts;
    };

    const setSort = (newSort: string) => {
        sort.value = newSort;
    };

    const setNextPage = () => {
        if (hasMoreResults()) {
            page.value = page.value + 1;
        }
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
    };

    const setCount = (newCount: number) => {
        productCount.value = newCount;
    };

    const setCategorySlug = (slug: string) => {
        categorySlug.value = slug;
    };

    const setPage = (newPage: number) => {
        page.value = newPage;
    };

    const setSelectedVariant = (variant: ProductVariantResponse) => {
        if (selectedVariant.value?.id === variant.id) return; //dont do anything if same

        selectedVariant.value = variant;

        events.emit("selectedVariantChanged", selectedVariant.value);
    };

    return {
        events,
        products,
        sort,
        page,
        perPage,
        perPageOptions,
        productCount,
        categorySlug,
        priceMin,
        priceMax,
        currentPrice,
        selectedVariant,
        layout,
        hasMoreResults,
        getProducts,
        getVariantProperties,
        setProducts,
        setSort,
        setNextPage,
        setPerPage,
        setCount,
        setCategorySlug,
        setPage,
        setSelectedVariant,
    };
});
